import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Space, Typography, Tooltip } from 'antd';
import type { FormInstance } from 'antd';

type Row = { address: string; amount: string };

/**
 * C 輪替名單編輯器:把「收款地址」「金額」兩個逗號字串,呈現成逐列(地址+金額)編輯,
 * 存回時序列化成兩個等長逗號字串(後端契約不變)。每列都有地址+金額 → 長度天生對齊,
 * 消除「兩串長度對不上 → C 靜默停用」的填寫錯誤。
 *
 * 隱藏 Form.Item(name)承載實際值參與送出;用 shouldUpdate render-prop 取反應式值
 * (此 antd 版本無 Form.useWatch)。rows 本地 state(打字順),只有外部載入/別處改
 * (依 mineRef 判斷)才重建。
 */
export function CRotationPairEditor(props: {
  form: FormInstance;
  receiversField: string;
  amountsField: string;
  label: string;
  tooltip?: string;
  readOnly?: boolean;
}) {
  const { form, receiversField, amountsField, label, tooltip, readOnly } = props;
  return (
    <Form.Item label={<Tooltip title={tooltip}>{label}</Tooltip>} style={{ marginBottom: 8 }}>
      {/* 隱藏欄位:實際值(參與送出) */}
      <Form.Item name={receiversField} hidden noStyle>
        <Input />
      </Form.Item>
      <Form.Item name={amountsField} hidden noStyle>
        <Input />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(p: any, c: any) =>
          p[receiversField] !== c[receiversField] || p[amountsField] !== c[amountsField]
        }
      >
        {(f: FormInstance) => (
          <InnerEditor
            form={form}
            receivers={f.getFieldValue(receiversField)}
            amounts={f.getFieldValue(amountsField)}
            receiversField={receiversField}
            amountsField={amountsField}
            readOnly={readOnly}
          />
        )}
      </Form.Item>
    </Form.Item>
  );
}

function InnerEditor(props: {
  form: FormInstance;
  receivers: any;
  amounts: any;
  receiversField: string;
  amountsField: string;
  readOnly?: boolean;
}) {
  const { form, receivers, amounts, receiversField, amountsField, readOnly } = props;
  const [rows, setRows] = useState<Row[]>([]);
  const mineRef = useRef<string>(' '); // 上次自己寫回的序列化;初值不等於任何真實值

  useEffect(() => {
    const serial = `${receivers ?? ''}|${amounts ?? ''}`;
    if (serial === mineRef.current) return; // 自己造成的變化 → 不覆蓋正在編輯的 rows
    const rs = String(receivers ?? '').split(',').map((s) => s.trim());
    const as = String(amounts ?? '').split(',').map((s) => s.trim());
    const n = Math.max(rs.length, as.length);
    const next: Row[] = [];
    for (let i = 0; i < n; i++) {
      const address = rs[i] ?? '';
      const amount = as[i] ?? '';
      if (address === '' && amount === '') continue;
      next.push({ address, amount });
    }
    setRows(next);
    mineRef.current = serial;
  }, [receivers, amounts]);

  const push = (next: Row[]) => {
    setRows(next); // UI 保留所有列(含剛新增未填的);序列化只取有效列 → receivers/amounts 必等長
    const valid = next.filter((r) => r.address.trim() !== '');
    const recv = valid.map((r) => r.address.trim()).join(',');
    const amt = valid.map((r) => r.amount.trim() || '0.01').join(','); // 兩者同源於 valid,長度天生相等
    mineRef.current = `${recv}|${amt}`;
    form.setFieldsValue({ [receiversField]: recv, [amountsField]: amt });
  };

  const setRow = (i: number, patch: Partial<Row>) =>
    push(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => push([...rows, { address: '', amount: '0.01' }]);
  const delRow = (i: number) => push(rows.filter((_, idx) => idx !== i));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={4}>
      {rows.length === 0 && (
        <Typography.Text type="secondary">（尚無名單，按下方「新增一列」）</Typography.Text>
      )}
      {rows.map((r, i) => (
        <Space key={i} align="baseline" wrap>
          <span style={{ color: '#999', width: 20, display: 'inline-block' }}>{i + 1}.</span>
          <Input
            placeholder="收款地址(T...)"
            value={r.address}
            onChange={(e) => setRow(i, { address: e.target.value })}
            readOnly={readOnly}
            style={{ width: 340 }}
          />
          <Input
            placeholder="金額 TRX"
            value={r.amount}
            onChange={(e) => setRow(i, { amount: e.target.value })}
            readOnly={readOnly}
            style={{ width: 110 }}
          />
          {!readOnly && (
            <a style={{ color: '#cf1322' }} onClick={() => delRow(i)}>
              刪
            </a>
          )}
        </Space>
      ))}
      {!readOnly && (
        <Button type="dashed" size="small" onClick={addRow}>
          + 新增一列
        </Button>
      )}
    </Space>
  );
}
